import { useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isAnalyzerPage as checkIsAnalyzerPage } from '@/message-analyzer/lib/analyzer-utils'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { RotateCcw, LogOut, X, ChevronDown, TrendingUp, Clock, Building2 } from 'lucide-react'
import { forceLocalSignOut, supabase } from '@/lib/supabase'
import { fullLogout } from '@/lib/storage'
import { Icon } from '@iconify/react'
import gear from '@iconify/icons-noto/gear'
import locked from '@iconify/icons-noto/locked'
import sparkles from '@iconify/icons-noto/sparkles'
import magnifyingGlass from '@iconify/icons-noto/magnifying-glass-tilted-right'
import { useAuth } from '@/contexts/AuthContext'
import { useActiveContext } from '@/contexts/ActiveContextProvider'
import { useEffectiveRole } from '@/contexts/QAPerspectiveContext'
import { KeyboardShortcutsModal } from '@lina/accessibility/components/KeyboardShortcutsModal'
import { changeLanguage } from '@/i18n'
import {
  listVisibleNavItems,
  isNavItemActiveSection,
  type AppNavItem,
} from '@/lib/routing/nav-items'
import { mapActualRoleToRouteRole, mapUserRoleToRouteRole } from '@/lib/routing/can-access-route'
import type { RouteId } from '@/lib/routing/types'

interface AppMenuProps {
  isOpen: boolean
  onClose: () => void
  isAdmin?: boolean
  onStartOver?: () => void
}

function NavItemIcon({ itemId }: { itemId: RouteId }) {
  switch (itemId) {
    case 'analyzer':
      return <Icon icon={sparkles} className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
    case 'lens':
      return <Icon icon={magnifyingGlass} className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
    case 'admin':
      return <Icon icon={locked} className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
    case 'org':
      return <Building2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
    default:
      return null
  }
}

export const AppMenu = ({ isOpen, onClose, onStartOver }: AppMenuProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showStartOverTooltip, setShowStartOverTooltip] = useState(false)
  const [showLanguageSelector, setShowLanguageSelector] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false)
  const { i18n, t } = useTranslation()

  const { user, accountData, isAnonymous } = useAuth()
  const effectiveRole = useEffectiveRole()
  const { activeContext, availableContexts } = useActiveContext()

  const actualRole = mapUserRoleToRouteRole(accountData?.role)
  const effective = mapActualRoleToRouteRole(effectiveRole)
  const isActualAdmin = accountData?.is_admin === true

  const visibleNavItems = useMemo(
    () =>
      listVisibleNavItems({
        actualRole,
        effectiveRole: effective,
        activeContextScope: activeContext?.scope ?? null,
        hasOrganizationContext: availableContexts.some((c) => c.scope === 'organization'),
        isAnonymous,
        isActualAdmin,
      }).filter((item) => !isNavItemActiveSection(item, location.pathname)),
    [
      actualRole,
      effective,
      activeContext?.scope,
      availableContexts,
      isAnonymous,
      isActualAdmin,
      location.pathname,
    ],
  )

  // Reset modal/tooltip states when menu closes
  useEffect(() => {
    if (!isOpen) {
      setShowStartOverTooltip(false)
      setShowLanguageSelector(false)
      setShowLogoutConfirm(false)
      setShowKeyboardShortcuts(false)
    }
  }, [isOpen])

  const userName = accountData?.first_name || ''
  const userEmail = user?.email || ''

  const isOnAnalyzerPage = checkIsAnalyzerPage(location.pathname)
  const isOnUserSettings = location.pathname === '/settings'
  const isOnFeaturePage =
    location.pathname.startsWith('/analyze') ||
    location.pathname.startsWith('/initiate') ||
    location.pathname.startsWith('/social-comment')
  const showRestartButton = isOnFeaturePage && onStartOver

  const handleNavItemClick = (item: AppNavItem) => {
    navigate(item.path)
    onClose()
  }

  const handleStartOver = () => {
    if (!onStartOver) return
    setShowStartOverTooltip(false)
    onStartOver()
  }

  const handleSignOut = async () => {
    setShowLogoutConfirm(false)
    fullLogout()
    try {
      await supabase.auth.signOut()
      await forceLocalSignOut()
    } catch (error) {
      console.warn('[AppMenu] signOut failed, continuing', error)
    } finally {
      window.location.assign('/auth/login')
    }
  }

  const handleNavigateToSettings = () => {
    const fromPath = `${location.pathname}${location.search}${location.hash}` || '/'
    navigate('/settings', { state: { from: fromPath } })
    onClose()
  }

  const navLabel = (item: AppNavItem) =>
    item.labelKey ? t(item.labelKey) : (item.label ?? item.id)

  if (!isOpen) return null

  const getInitials = () => {
    if (userName) return userName.charAt(0).toUpperCase()
    if (userEmail) return userEmail.charAt(0).toUpperCase()
    return 'U'
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[45] animate-in fade-in duration-200 pointer-events-auto"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }}
        style={{ width: '100vw', height: '100vh', top: 0, left: 0 }}
      />

      <div
        className="fixed top-0 right-0 w-50 sm:w-72 md:w-64 glass-bordered rounded-bl-xl z-50 animate-in slide-in-from-right duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 flex flex-col bg-background/80 backdrop-blur-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 shrink-0">
              {showRestartButton && (
                <div className="relative">
                  <Button
                    onClick={() => setShowStartOverTooltip(!showStartOverTooltip)}
                    size="icon"
                    variant="ghost"
                    className="p-1.5 sm:p-2 hover:text-primary btn-primary-glow transition-colors"
                    aria-label={t('appMenu.restartAnalysis')}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  {showStartOverTooltip && (
                    <div className="absolute top-full left-0 mt-2 w-56 sm:w-64 glass bg-background rounded-lg p-3 z-[70] animate-in fade-in slide-in-from-top-2 duration-200">
                      <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-3">
                        {t('appMenu.restartPrompt')}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowStartOverTooltip(false)}
                          className="flex-1 text-xs rounded-lg"
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleStartOver}
                          className="flex-1 text-xs rounded-lg text-white bg-primary hover:bg-primary/70 btn-primary-glow"
                        >
                          {t('appMenu.restart')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="relative shrink-0">
                <Button
                  onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                  variant="ghost"
                  className="p-2 w-20 px-2.5 rounded-2xl glass-subtle btn-primary-glow hover:text-primary transition-colors text-center items-center"
                  aria-label={t('common.language')}
                >
                  <span className="text-xs sm:text-sm font-semibold tracking-wide">
                    {i18n.language === 'fr-CA' ? '🇨🇦 FR' : '🇬🇧 EN'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
                {showLanguageSelector && (
                  <div className="absolute top-full left-0 mt-2 w-[184px] glass bg-background rounded-lg p-2 z-[70] animate-in fade-in slide-in-from-top-2 duration-200 border border-gray-200 dark:border-gray-700 shadow-lg">
                    <button
                      type="button"
                      className={`w-full text-left rounded-md px-3 py-2 text-sm font-medium transition-colors ${i18n.language === 'fr-CA' ? 'text-gray-700 dark:text-gray-200 hover:bg-primary/10' : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                      onClick={async () => {
                        await changeLanguage('en')
                        setShowLanguageSelector(false)
                      }}
                    >
                      🇬🇧 English
                    </button>
                    <button
                      type="button"
                      className={`w-full text-left rounded-md px-3 py-2 text-sm font-medium transition-colors ${i18n.language === 'fr-CA' ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200 hover:bg-primary/10'}`}
                      onClick={async () => {
                        await changeLanguage('fr-CA')
                        setShowLanguageSelector(false)
                      }}
                    >
                      🇨🇦 French (CA)
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={onClose}
              size="icon"
              variant="ghost"
              className="p-1.5 sm:p-2 hover:text-red-500 dark:hover:text-red-400 btn-red-glow transition-colors"
              aria-label={t('appMenu.closeMenu')}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex flex-col items-center space-y-2 sm:space-y-3 flex-1">
            <button onClick={handleNavigateToSettings} className="group">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-gradient-accent flex items-center justify-center text-white text-lg sm:text-xl font-bold transition-transform group-hover:scale-105">
                {getInitials()}
              </div>
            </button>
            <button
              onClick={handleNavigateToSettings}
              className="text-center hover:text-primary transition-colors"
            >
              {userName ? (
                <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100">
                  {userName}
                </p>
              ) : (
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{userEmail}</p>
              )}
            </button>
          </div>

          <div className="border-t border-gray-200 dark:border-border"></div>

          {visibleNavItems.map((item) => (
            <Button
              key={item.id}
              onClick={() => handleNavItemClick(item)}
              variant="ghost"
              size="sm"
              className="w-full hover:glass transition-shadow text-xs sm:text-sm"
            >
              <NavItemIcon itemId={item.id} />
              {navLabel(item)}
            </Button>
          ))}

          {!isOnUserSettings && (
            <Button
              onClick={handleNavigateToSettings}
              variant="ghost"
              size="sm"
              className="w-full hover:glass transition-shadow text-xs sm:text-sm"
            >
              <Icon icon={gear} className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              {t('appMenu.userSettings')}
            </Button>
          )}

          <div className="border-t border-gray-200 dark:border-border"></div>

          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full flex items-center justify-center gap-2 py-2 text-fluid-xs text-gray-500 hover:text-red-600 hover:font-bold transition-colors mt-auto"
          >
            <LogOut className="w-3 h-3" />
            {t('common.signOut')}
          </button>
        </div>
      </div>

      {showLogoutConfirm && (
        <Modal open={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)}>
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100/20 backdrop-blur-sm border border-red-300/30 flex items-center justify-center">
              <LogOut className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-fluid-xl font-bold text-foreground">{t('appMenu.signOutTitle')}</h3>
            <p className="text-fluid-sm text-muted-foreground">{t('appMenu.signOutPrompt')}</p>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 rounded-xl"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSignOut}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
              >
                {t('common.signOut')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <KeyboardShortcutsModal
        open={showKeyboardShortcuts}
        onOpenChange={setShowKeyboardShortcuts}
      />
    </>
  )
}
